import React from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@components/ui/dialog";
import { useQuery } from "react-query";
import { twoFactorAuth } from "@services/api/home";
import { Button } from "@components/ui/button";

const getRemainingDays = (dateStr: string | null): number => {
  if (!dateStr) return 0;
  const today = new Date();
  const targetDate = new Date(dateStr);
  const diff = targetDate.getTime() - today.getTime();
  return Math.ceil(diff / (1000 * 3600 * 24));
};

const TwoFactorAuth = () => {
  const todayStr = new Date().toDateString();

  const popupDisplayTime_pw = sessionStorage.getItem("ls__passwordExpiry");
  const popupDisplayTime_uv = sessionStorage.getItem("ls__userValidity");

  const passwordDayDiff = getRemainingDays(popupDisplayTime_pw);
  const userDayDiff = getRemainingDays(popupDisplayTime_uv);

  const [showPasswordExpiry, setShowPasswordExpiry] = React.useState(
    !popupDisplayTime_pw || passwordDayDiff >= 1,
  );
  const [showUserValidity, setShowUserValidity] = React.useState(
    !popupDisplayTime_uv || userDayDiff >= 1,
  );

  React.useEffect(() => {
    setShowUserValidity(!popupDisplayTime_uv || userDayDiff >= 1);
  }, [popupDisplayTime_uv, userDayDiff]);

  React.useEffect(() => {
    setShowPasswordExpiry(!popupDisplayTime_pw || passwordDayDiff >= 1);
  }, [popupDisplayTime_pw, passwordDayDiff]);

  const { data: authData } = useQuery(["twoFactorAuth"], () => twoFactorAuth());

  const [alertsQueue, setAlertsQueue] = React.useState<any[]>([]);
  const [currentAlert, setCurrentAlert] = React.useState<any | null>(null);

  React.useEffect(() => {
    const newAlerts: any[] = [];

    if (authData?.otpStatus === "Enabled") {
      newAlerts.push({
        Heading: "Two Factor Authentication",
        Message:
          "Two Factor Authentication is enabled for your account. Please complete the setup.",
        Link: `${process.env.API_URL}/settings/#change-password`,
        LinkText: "Proceed to Setup",
      });
    }

    if (
      authData?.passwordExpiry &&
      authData?.passwordExpiry > -1 &&
      showPasswordExpiry
    ) {
      const text =
        authData.passwordExpiry > 1
          ? `in ${authData.passwordExpiry} days`
          : authData.passwordExpiry === 1
            ? "tomorrow"
            : "today";

      newAlerts.push({
        Heading: "Password Expiry Alert",
        Message: `Your password will expire ${text}. Please change your password.`,
        Link: `${process.env.API_URL}/settings/#change-password`,
        LinkText: "Reset Password",
      });
    }

    if (
      authData?.userValidity &&
      authData?.userValidity > -1 &&
      showUserValidity
    ) {
      const text =
        authData.userValidity > 1
          ? `in ${authData.userValidity} days`
          : authData.userValidity === 1
            ? "tomorrow"
            : "today";

      newAlerts.push({
        Heading: "User Validity Alert",
        Message: `Your account will expire ${text}. Please contact your administrator.`,
        Link: "",
        LinkText: "",
      });
    }

    if (newAlerts.length > 0) {
      setAlertsQueue(newAlerts);
      setCurrentAlert(newAlerts[0]);
    }
  }, [authData, showPasswordExpiry, showUserValidity]);

  const handleClose = (heading: string) => {
    if (alertsQueue.length > 1) {
      const remaining = alertsQueue.slice(1);
      setAlertsQueue(remaining);
      setCurrentAlert(remaining[0]);
    } else {
      setCurrentAlert(null);
    }
    setSessionStorage(heading);
  };

  const setSessionStorage = (heading: string) => {
    if (heading === "Password Expiry Alert") {
      sessionStorage.setItem("ls__passwordExpiry", todayStr);
    }
    if (heading === "User Validity Alert") {
      sessionStorage.setItem("ls__userValidity", todayStr);
    }
  };

  return (
    <>
      <Dialog
        open={!!currentAlert}
        onOpenChange={() => handleClose(currentAlert?.Heading || "")}
      >
        <DialogContent className="sm:max-w-[30vw] [&>.dialog-close-btn]:hidden">
          <DialogHeader className="font-bold text-primary underline underline-offset-4">
            {currentAlert?.Heading}
          </DialogHeader>
          <div>{currentAlert?.Message}</div>
          <DialogFooter>
            {currentAlert?.LinkText && (
              <Button
                variant="default"
                onClick={() => {
                  currentAlert?.Link
                    ? window.location.replace(currentAlert.Link)
                    : handleClose(currentAlert?.Heading || "");

                  setSessionStorage(currentAlert?.Heading || "");
                }}
              >
                {currentAlert.LinkText}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => handleClose(currentAlert?.Heading || "")}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TwoFactorAuth;
